<template>
  <!-- Confirmation page -->
  <div class="order-form-container">
    <Panel rounded bordered filled>
      <PanelHeader rounded no-background bordered>
        <Heading level="3" weight="bold"> Confirm Purchase </Heading>
      </PanelHeader>
      <PanelBody v-if="instrument" padding="4.2rem 2.2rem">
        <Alert v-if="errorMessage" variant="error">
          <Heading level="3" no-margin>{{ errorMessage }}</Heading>
          <p v-if="errorMessageDescription" class="error-description">
            {{ errorMessageDescription }}
          </p>
        </Alert>

        <Flex
          class="order-form-basic-confirm-container"
          direction="column"
          justify-content="center"
        >
          <Flex
            class="graphics-container"
            direction="row"
            align-items="center"
            justify-content="center"
          >
            <PropGroup hide-label no-margin size="75px">
              <template slot-scope="props">
                <CurrencyIcon v-bind="props" :symbol="productFrom.Product" />
                <Icon
                  :icon="
                    require('-!vue-svg-loader!../../../assets/images/icons/icon-arrow-right-alternative.svg')
                  "
                  width="40px"
                  class="arrow-icon"
                />
                <CurrencyIcon v-bind="props" :symbol="productTo.Product" />
              </template>
            </PropGroup>
          </Flex>

          <Heading level="3" align="center">
            {{
              side === 'buy'
                ? $t('exchange.order-form-basic.totals-main.you-are-buying')
                : $t('exchange.order-form-basic.totals-main.you-are-selling')
            }}
            {{ amountPrimary | formatNumber(productPrimary.DecimalPlaces) }}
            {{ productPrimary.Product }}
            {{ $t('exchange.order-form-basic.confirm.for') }}
            {{ totalSecondary | formatNumber(productSecondary.DecimalPlaces) }}
            {{ productSecondary.Product }}
          </Heading>

          <Heading class="light-text" level="4" align="center">
            ({{ fee | formatNumber(feeProduct.DecimalPlaces) }}
            {{ feeProduct.Product }} {{ $t('exchange.common.fee') }}
            {{ $t('exchange.common.inclusive') }})
          </Heading>

          <GridContainer template-columns="1fr 1fr" class="buttons-container">
            <PropGroup variant="primary" size="large" block rounded>
              <template slot-scope="props">
                <ButtonBase
                  v-bind="props"
                  :disabled="isOrderLoading"
                  class="button-cancel"
                  outlined
                  @click="actionCancel()"
                >
                  {{ $t('exchange.common.cancel') }}
                </ButtonBase>
                <ButtonBase
                  v-bind="props"
                  :disabled="isOrderLoading || !!errorMessage"
                  :loading="isOrderLoading"
                  class="button-confirm"
                  @click="submitOrder()"
                >
                  {{
                    side === 'buy'
                      ? $t('exchange.order-form-basic.confirm.place-buy-order')
                      : $t('exchange.order-form-basic.confirm.place-sell-order')
                  }}
                </ButtonBase>
              </template>
            </PropGroup>
          </GridContainer>
        </Flex>
      </PanelBody>
    </Panel>
  </div>
</template>

<script>
import {
  Flex,
  Heading,
  GridContainer,
  Icon,
  ButtonBase,
  PropGroup,
  Panel,
  PanelHeader,
  PanelBody,
  Alert,
} from '@nauticus-blockchain/nauticus-ui'
import CurrencyIcon from '@/components/common/CurrencyIcon'

export default {
  name: 'OrderFormBasicConfirm',
  components: {
    Flex,
    Heading,
    GridContainer,
    Icon,
    CurrencyIcon,
    ButtonBase,
    PropGroup,
    Panel,
    PanelHeader,
    PanelBody,
    Alert,
  },
  data() {
    return {
      orderWasRejected: false,
    }
  },
  computed: {
    side() {
      return this.$store.state.exchange.orderFormBasic.side
    },

    instrument() {
      return this.$store.state.exchange.orderFormBasic.instrument
    },

    amountPrimary() {
      return this.$store.state.exchange.orderFormBasic.amountPrimary
    },
    amountSecondary() {
      return this.$store.state.exchange.orderFormBasic.amountSecondary
    },

    productPrimary() {
      // Aka: quantitySecondaryCurrency
      if (!this.instrument) return null
      return this.$store.getters['products/getProductById'](
        this.instrument.Product1
      )
    },
    productSecondary() {
      // Aka: quantitySecondary
      if (!this.instrument) return null
      return this.$store.getters['products/getProductById'](
        this.instrument.Product2
      )
    },

    productFrom() {
      return this.side === 'buy' ? this.productSecondary : this.productPrimary
    },
    productTo() {
      return this.side === 'buy' ? this.productPrimary : this.productSecondary
    },

    totalPrimary() {
      return this.$store.state.exchange.orderFormBasic.totalPrimary
    },
    totalSecondary() {
      return this.$store.state.exchange.orderFormBasic.totalSecondary
    },

    fee() {
      return this.$store.getters['exchange/orderFormBasic/fee']
    },
    feeProduct() {
      return this.$store.getters['exchange/orderFormBasic/feeProduct']
    },

    sourceOfTruth() {
      return this.$store.state.exchange.orderFormBasic.sourceOfTruth
    },

    isOrderLoading() {
      return this.$store.state.exchange.orderFormBasic.isOrderLoading
    },

    orderId() {
      return this.$store.state.exchange.orderFormBasic.orderId
    },

    completedOrder() {
      const orderData = this.$store.state.exchange.orderFormBasic.completedOrder

      if (orderData) return orderData

      return this.$store.state.openOrders.orders.filter(
        order =>
          order.ClientOrderId ===
          this.$store.state.exchange.orderFormBasic.orderId
      )[0]
    },

    rejectedOrders() {
      return this.$store.state.openOrders.rejectedOrders
    },

    ordersForSide() {
      return this.side === 'buy'
        ? this.$store.getters['exchange/market/bids'](100)
        : this.$store.getters['exchange/market/asks'](100)
    },

    orderValid() {
      return this.$store.getters['exchange/orderFormBasic/orderValid']
    },

    errorMessage() {
      switch (this.orderValid) {
        case 'NoInstrument':
          return this.$t('exchange.order-form-basic.errors.no-instrument.title')

        case 'NoMarket':
          return this.$t('exchange.order-form-basic.errors.no-market.title')

        case 'InsufficientMarket':
          return this.$t(
            'exchange.order-form-basic.errors.insufficient-market.title'
          )

        case 'InsufficientBalancePrimary':
        case 'InsufficientBalanceSecondary':
          return this.$t(
            'exchange.order-form-basic.errors.insufficient-balace.title'
          )
      }
    },

    errorMessageDescription() {
      switch (this.orderValid) {
        case 'NoInstrument':
          return this.$t(
            'exchange.order-form-basic.errors.no-instrument.description',
            { side: this.side }
          )

        case 'NoMarket':
          return this.$t(
            'exchange.order-form-basic.errors.no-market.description',
            {
              pair: `${this.productPrimary.Product}/${
                this.productSecondary.Product
              }`,
            }
          )

        case 'InsufficientMarket':
          return this.$t(
            'exchange.order-form-basic.errors.insufficient-market.description'
          )

        case 'InsufficientBalancePrimary':
        case 'InsufficientBalanceSecondary':
          return this.$t(
            'exchange.order-form-basic.errors.insufficient-balace.description'
          )
      }
    },
  },
  watch: {
    rejectedOrders(newValue) {
      if (!newValue) return
      if (newValue.some(order => order.ClientOrderId === this.orderId)) {
        this.orderWasRejected = true
        this.$store.commit('exchange/orderFormBasic/setIsOrderLoading', false)
      }
    },
    completedOrder(newValue) {
      if (newValue) {
        this.$store.commit(
          'exchange/orderFormBasic/setCompletedOrder',
          newValue
        )
        this.$store.commit('exchange/orderFormBasic/setIsOrderLoading', false)
        this.$router.replace({ name: 'orderFormCompleted' })
      }
    },
    ordersForSide() {
      this.$store.dispatch('exchange/orderFormBasic/updateAmount')
    },
  },
  mounted() {
    if (!this.instrument) this.$router.replace({ name: 'orderFormBasic' })
  },
  methods: {
    submitOrder() {
      this.$store.dispatch('exchange/orderFormBasic/placeOrder')
    },
    actionCancel() {
      this.$router.replace({ name: 'orderFormBasic' })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@nauticus-blockchain/nauticus-ui/src/assets/styles/variables';
@import '~@nauticus-blockchain/nauticus-ui/src/assets/styles/mixins';

.order-form-container {
  margin: 6rem auto;
  width: 88rem;
}
.blue-text {
  color: $blue;
}

.light-text {
  color: var(--basic-trade-confirm-fee_text-color);
}

.order-form-basic-confirm-container {
  text-align: center;
}

.graphics-container {
  margin-top: 22px;
  margin-bottom: 20px;
}

.buttons-container {
  margin-top: 32px;
  max-width: 600px;
  margin: 32px auto;

  .button-cancel {
    margin-right: 15px;
  }
  .button-confirm {
    margin-left: 15px;
  }
}

.arrow-icon {
  margin-left: 48px;
  margin-right: 48px;
}

.error-description {
  font-size: 1.6rem;
  margin: 0;
}
</style>
